import { ScenarioPhase, ScenarioImpact, AffectedProject } from "../types";

// Mock data
const MOCK_PHASES: ScenarioPhase[] = [
    { id: "1", name: "Work Request", code: "WR", isSelected: false },
    { id: "2", name: "Estimate / Validation", code: "SG1", isSelected: false },
    { id: "3", name: "Feasibility / Conceptual Design", code: "FCD", isSelected: false },
    { id: "4", name: "Detailed Design", code: "DD", isSelected: false },
    { id: "5", name: "Pre-Construction", code: "SG3", isSelected: false },
    { id: "6", name: "Construction", code: "SG4", isSelected: true },
    { id: "7", name: "Commissioning", code: "SG5", isSelected: false },
    { id: "8", name: "Closure", code: "CL", isSelected: false },
];

const MOCK_AFFECTED_PROJECTS: AffectedProject[] = [
    { id: "1", code: "CDL-0209", name: "Capacity Upgrade – Refurb Station A", phase: "Construction (SG4)", status: "active" },
    { id: "2", code: "CDL-0199", name: "Resilience – Refurb 27", phase: "Construction (SG4)", status: "active" },
    { id: "3", code: "CDL-0196", name: "Asset Health – Extension Phase 2", phase: "Construction (SG4)", status: "active" },
    { id: "4", code: "CDL-0188", name: "Resilience – Extension Works Phase 1", phase: "Construction (SG4)", status: "active" },
    { id: "5", code: "CDL-0209", name: "Capacity Upgrade – Refurb Station A", phase: "Construction (SG4)", status: "active" },
    { id: "6", code: "CDL-0199", name: "Resilience – Refurb 27", phase: "Construction (SG4)", status: "active" },
    { id: "7", code: "CDL-0196", name: "Asset Health – Extension Phase 2", phase: "Construction (SG4)", status: "active" },
    { id: "8", code: "CDL-0188", name: "Resilience – Extension Works Phase 1", phase: "Construction (SG4)", status: "active" },
];

// Mock API functions
export const getScenarioPhases = async (): Promise<ScenarioPhase[]> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    return MOCK_PHASES;
};

export const getAffectedProjects = async (phaseIds: string[]): Promise<AffectedProject[]> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));
    
    // Filter projects based on selected phases (for mock purposes, return all)
    return MOCK_AFFECTED_PROJECTS;
};

export const calculateScenarioImpact = async (
    delayDuration: number,
    selectedPhaseIds: string[]
): Promise<ScenarioImpact> => {
    // Simulate longer API delay for "thinking" effect
    await new Promise((resolve) => setTimeout(resolve, 2200));

    const phaseCount = selectedPhaseIds.length;
    const integrityRisks = Math.floor(delayDuration / 21) + phaseCount;
    const budgetAtRisk = delayDuration * 40000 * Math.max(1, phaseCount * 0.6);

    return {
        newIntegrityRisks: integrityRisks,
        budgetAtRisk,
        criticalPathIssues: phaseCount > 0 ? Math.min(phaseCount, 4) : 0,
        scheduleOverrun: delayDuration + Math.floor(delayDuration * 0.3 * phaseCount),
        resourceConflicts: Math.floor(delayDuration / 14) * phaseCount,
        materialDelays: Math.ceil(phaseCount * 1.5),
        supplierRisks: Math.floor(delayDuration / 30) + 1,
        regulatoryExposure: delayDuration > 28 ? Math.floor(delayDuration / 28) : 0,
        overallRiskScore: Math.min(98, Math.round(20 + (delayDuration / 7) * 8 + phaseCount * 6)),
        cascadeEffects: [
            { area: 'Supply Chain', severity: delayDuration > 30 ? 'critical' : 'high', description: `${Math.ceil(phaseCount * 1.5)} material orders require rescheduling, potential price escalation of £${Math.round(delayDuration * 2800).toLocaleString()}`, affectedProjects: Math.ceil(phaseCount * 2) },
            { area: 'Workforce', severity: delayDuration > 21 ? 'high' : 'medium', description: `${Math.floor(delayDuration / 14) * phaseCount} resource conflicts across ${phaseCount} phases. Crew reallocation required.`, affectedProjects: phaseCount * 2 },
            { area: 'Regulatory & Permits', severity: delayDuration > 28 ? 'high' : 'low', description: delayDuration > 28 ? `${Math.floor(delayDuration / 28)} permits may expire before revised completion. Re-application costs ~£${(Math.floor(delayDuration / 28) * 15000).toLocaleString()}` : 'Current permits remain valid within tolerance', affectedProjects: delayDuration > 28 ? Math.floor(delayDuration / 28) * 2 : 0 },
            { area: 'Client Commitments', severity: integrityRisks > 2 ? 'critical' : 'medium', description: `${integrityRisks} projects breach contractual milestone dates. Liquidated damages exposure: £${Math.round(integrityRisks * 45000).toLocaleString()}`, affectedProjects: integrityRisks },
            { area: 'Environmental', severity: 'medium', description: `Seasonal window risk — ${Math.ceil(phaseCount * 0.4)} projects may miss environmental consent windows if delayed beyond ${delayDuration + 14} days`, affectedProjects: Math.ceil(phaseCount * 0.4) },
        ],
        recommendations: [
            `Accelerate ${Math.min(3, phaseCount)} critical path projects by deploying additional crew resource`,
            `Re-sequence ${Math.ceil(phaseCount * 0.5)} non-critical projects to free capacity for priority work`,
            delayDuration > 21 ? `Engage supply chain partners immediately on material re-scheduling to avoid price escalation` : `Monitor material lead times — no immediate action required`,
            integrityRisks > 1 ? `Notify client programme board of potential milestone breach on ${integrityRisks} projects` : `No client notification required at this stage`,
            `Update resource model to reflect ${Math.floor(delayDuration / 14) * phaseCount} workforce conflicts`,
            delayDuration > 28 ? `Begin permit extension applications for ${Math.floor(delayDuration / 28)} affected consents` : `Permits remain within validity — no action needed`,
        ].filter(r => !r.includes('no immediate action') && !r.includes('No client notification') && !r.includes('no action needed')),
    };
};
